import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const DiagnosticPage = () => {
  const { user, currentTenant, isGlobalAdmin, isCompanyAdmin, canWrite, isViewer, availableCompanies } = useAuth();
  const [localStorageData, setLocalStorageData] = useState({});
  const [tokenInfo, setTokenInfo] = useState(null);

  useEffect(() => {
    // Leer todo localStorage
    const storage = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      storage[key] = localStorage.getItem(key);
    }
    setLocalStorageData(storage);

    // Decodificar JWT
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setTokenInfo(payload);
      } catch (e) {
        setTokenInfo({ error: 'Token inválido' });
      }
    }
  }, []);

  const handleClearAndLogout = () => {
    if (window.confirm('¿Segura que quieres cerrar sesión y limpiar todos los datos?')) {
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold mb-6 text-gray-800">🔍 Diagnóstico de Sesión</h1>
          
          {/* Usuario actual */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h2 className="text-xl font-bold mb-3 text-blue-800">👤 Usuario Actual</h2>
            <div className="space-y-2 text-sm">
              <p><strong>Email:</strong> {user?.email || 'No autenticado'}</p>
              <p><strong>Nombre:</strong> {user?.name || 'N/A'}</p>
              <p><strong>Rol Global:</strong> <span className="font-mono bg-gray-200 px-2 py-1 rounded">{user?.role || 'N/A'}</span></p>
              <p><strong>ID:</strong> {user?.id || 'N/A'}</p>
            </div>
          </div>

          {/* Empresa actual */}
          <div className="mb-6 p-4 bg-purple-50 rounded-lg">
            <h2 className="text-xl font-bold mb-3 text-purple-800">🏢 Empresa Seleccionada</h2>
            <div className="space-y-2 text-sm">
              <p><strong>Tenant Actual:</strong> <span className="font-mono bg-gray-200 px-2 py-1 rounded text-lg">{currentTenant || 'N/A'}</span></p>
              <p><strong>¿Es Admin Global?</strong> {isGlobalAdmin ? '✅ Sí' : '❌ No'}</p>
              <p><strong>¿Es Admin de Empresa?</strong> {isCompanyAdmin ? '✅ Sí' : '❌ No'}</p>
              <p><strong>¿Puede Escribir?</strong> {canWrite ? '✅ Sí' : '❌ No'}</p>
              <p><strong>¿Es Viewer?</strong> {isViewer ? '✅ Sí' : '❌ No'}</p>
            </div>
          </div>

          {/* Empresas disponibles */}
          <div className="mb-6 p-4 bg-green-50 rounded-lg">
            <h2 className="text-xl font-bold mb-3 text-green-800">🌎 Empresas Disponibles</h2>
            <div className="space-y-2">
              {availableCompanies && availableCompanies.length > 0 ? (
                availableCompanies.map(company => (
                  <div key={company.id} className={`p-3 rounded-lg ${company.id === currentTenant ? 'bg-green-200 border-2 border-green-500' : 'bg-white border border-gray-300'}`}>
                    <p className="font-bold">{company.name}</p>
                    <p className="text-sm">ID: <span className="font-mono">{company.id}</span></p>
                    <p className="text-sm">Rol: <span className="font-mono bg-gray-200 px-2 py-1 rounded">{company.role}</span></p>
                    {company.id === currentTenant && <p className="text-xs text-green-700 mt-1">✅ Seleccionada actualmente</p>}
                  </div>
                ))
              ) : (
                <p className="text-red-600">⚠️ No hay empresas disponibles</p>
              )}
            </div>
          </div>

          {/* Token JWT */}
          <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
            <h2 className="text-xl font-bold mb-3 text-yellow-800">🔐 Token JWT</h2>
            {tokenInfo ? (
              <div className="space-y-2 text-sm">
                <p><strong>User ID:</strong> {tokenInfo.userId || tokenInfo.id}</p>
                <p><strong>Email:</strong> {tokenInfo.email}</p>
                <p><strong>Rol:</strong> <span className="font-mono bg-gray-200 px-2 py-1 rounded">{tokenInfo.role}</span></p>
                <p><strong>Emitido:</strong> {tokenInfo.iat ? new Date(tokenInfo.iat * 1000).toLocaleString() : 'N/A'}</p>
                <p><strong>Expira:</strong> {tokenInfo.exp ? new Date(tokenInfo.exp * 1000).toLocaleString() : 'N/A'}</p>
                {tokenInfo.error && <p className="text-red-600">{tokenInfo.error}</p>}
              </div>
            ) : (
              <p className="text-red-600">⚠️ No hay token</p>
            )}
          </div>

          {/* localStorage */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-bold mb-3 text-gray-800">💾 localStorage</h2>
            <div className="space-y-1 text-sm font-mono">
              {Object.entries(localStorageData).map(([key, value]) => (
                <div key={key} className="border-b border-gray-200 py-1">
                  <strong className="text-blue-600">{key}:</strong> 
                  <span className="ml-2 text-gray-700">{value.length > 100 ? value.substring(0, 100) + '...' : value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Diagnóstico */}
          <div className="p-4 bg-red-50 rounded-lg border-2 border-red-300">
            <h2 className="text-xl font-bold mb-3 text-red-800">⚠️ Problemas Detectados</h2>
            <ul className="space-y-2 text-sm">
              {user?.role === 'company_admin' && (
                <li className="flex items-start gap-2">
                  <span className="text-2xl">❌</span>
                  <div>
                    <strong>Rol global obsoleto:</strong> Tienes <code className="bg-red-200 px-1 rounded">company_admin</code> que es DEPRECADO. 
                    Debería ser <code className="bg-green-200 px-1 rounded">agent</code>. 
                    <strong className="text-red-700"> Debes cerrar sesión y volver a entrar.</strong>
                  </div>
                </li>
              )}
              {currentTenant === 'chirakChile' && user?.email === 'ana@chirak.pe' && (
                <li className="flex items-start gap-2">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <strong>Tenant incorrecto:</strong> Estás viendo Chile pero tu email es de Perú. 
                    Cambia a "Chirak Turismo Perú" usando el selector del header.
                  </div>
                </li>
              )}
              {!isCompanyAdmin && !isGlobalAdmin && user && (
                <li className="flex items-start gap-2">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <strong>Sin permisos de admin:</strong> No verás el botón "Admin" ni podrás gestionar recursos.
                  </div>
                </li>
              )}
            </ul>
          </div>

          {/* Botón de limpieza */}
          <div className="mt-6 flex gap-4">
            <button
              onClick={handleClearAndLogout}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            >
              🔄 Limpiar Todo y Cerrar Sesión
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            >
              ↻ Recargar Página
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticPage;
